import React, { useEffect, useMemo, useState } from "react";
import { MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  locationSupport,
  shouldPromptLocation,
  setLocationDecision,
  getCurrentPositionOnce,
} from "@/lib/location";
import { isInstallStepSettled, isStandalone } from "@/lib/install";
import { isPushStepSettled } from "@/lib/push";

const COPY = {
  en: {
    kicker: "One last permission",
    title: "Let Andreas find you (kindly)",
    body: "It\u2019s important for the wedding — with your location Andreas can welcome you to Cyprus, nudge you as you approach Ammos Kalambouri, and celebrate when you arrive.",
    highlight: "Only checked while the app is open. Never sold, never kept beyond the wedding.",
    allow: "Share my location",
    later: "Maybe later",
    granted: "Thank you, koumbare. Safe travels.",
    denied: "No worries — you can turn location on later in your browser settings.",
  },
  fr: {
    kicker: "Une dernière autorisation",
    title: "Laisse Andreas te retrouver (gentiment)",
    body: "C\u2019est important pour le mariage — avec ta position Andreas peut t\u2019accueillir à Chypre, te faire signe en approchant d\u2019Ammos Kalambouri, et fêter ton arrivée.",
    highlight: "Vérifié uniquement quand l\u2019app est ouverte. Jamais vendu, jamais gardé après le mariage.",
    allow: "Partager ma position",
    later: "Plus tard",
    granted: "Merci, koumbare. Bon voyage.",
    denied: "Pas de souci — tu peux activer plus tard dans les paramètres du navigateur.",
  },
  el: {
    kicker: "Μια τελευταία άδεια",
    title: "Άσε τον Andreas να σε βρει (ευγενικά)",
    body: "Είναι σημαντικό για τον γάμο — με τη θέση σου ο Andreas σε καλωσορίζει στην Κύπρο, σου νεύει καθώς πλησιάζεις στον Άμμο Καλαμπούρη, και γιορτάζει όταν φτάσεις.",
    highlight: "Ελέγχεται μόνο όταν η εφαρμογή είναι ανοιχτή. Δεν πωλείται, δεν μένει μετά τον γάμο.",
    allow: "Μοιράσου τη θέση μου",
    later: "Ίσως αργότερα",
    granted: "Ευχαριστούμε, κουμπάρε. Καλό ταξίδι.",
    denied: "Κανένα πρόβλημα — ενεργοποίησε αργότερα από τις ρυθμίσεις του browser.",
  },
  pl: {
    kicker: "Ostatnia zgoda",
    title: "Pozwól Andreasowi cię znaleźć (grzecznie)",
    body: "To ważne dla wesela — z twoją lokalizacją Andreas powita cię na Cyprze, mrugnie gdy zbliżysz się do Ammos Kalambouri, i Życzy szczęścia gdy dotrzesz.",
    highlight: "Sprawdzane tylko gdy aplikacja jest otwarta. Nie sprzedajemy, nie zachowujemy po weselu.",
    allow: "Udostępnij lokalizację",
    later: "Może później",
    granted: "Dzięki, kumie. Szczęśliwej drogi.",
    denied: "Żadnego problemu — możesz włączyć później w ustawieniach przeglądarki.",
  },
};

const LocationPromptModal = () => {
  const { lang } = useLang();
  const { isAuthed, authReady } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const c = useMemo(() => COPY[lang] || COPY.en, [lang]);

  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (!locationSupport()) return;
    let interval;
    const tryOpen = async () => {
      if (!isInstallStepSettled() || !isPushStepSettled()) return;
      // Inside the installed PWA, PwaWelcomeModal owns this flow — stand aside.
      if (isStandalone()) {
        const welcomeState = (() => { try { return localStorage.getItem("em_pwa_welcome_state"); } catch { return null; } })();
        if (welcomeState !== "never" && welcomeState !== "done") {
          clearInterval(interval);
          return;
        }
      }
      const should = await shouldPromptLocation();
      if (should) setOpen(true);
      clearInterval(interval);
    };
    const start = setTimeout(() => {
      tryOpen();
      interval = setInterval(tryOpen, 900);
    }, 4200);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [authReady, isAuthed]);

  const onAllow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const pos = await getCurrentPositionOnce();
      if (pos && pos.coords) {
        setLocationDecision("granted");
        toast.success(c.granted);
        setOpen(false);
      }
    } catch (err) {
      // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3
      if (err && err.code === 1) {
        setLocationDecision("denied");
        toast.error(c.denied);
      } else {
        setLocationDecision("later");
      }
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const onLater = () => {
    setLocationDecision("later");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[285] flex items-end sm:items-center justify-center bg-foreground/45 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="location-modal"
    >
      <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onLater}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5"
          aria-label="Close"
          data-testid="location-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-6 sm:p-7">
          <div className="w-12 h-12 rounded-full bg-accent/15 text-accent flex items-center justify-center mb-4">
            <MapPin className="w-5 h-5" />
          </div>
          <p className="editorial-kicker text-foreground/50" data-testid="location-kicker">{c.kicker}</p>
          <h2 className="font-serif-display text-2xl sm:text-[28px] leading-tight mt-2" data-testid="location-title">{c.title}</h2>
          <p className="text-sm sm:text-[15px] leading-relaxed text-foreground/75 mt-3" data-testid="location-body">{c.body}</p>
          <div className="mt-4 flex items-start gap-2 rounded-md border border-accent/30 bg-accent/[0.08] px-3 py-2">
            <span className="text-[11px] text-foreground/80">{c.highlight}</span>
          </div>
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={onLater}
              className="h-11 px-5 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors text-[13px] tracking-[0.14em] uppercase"
              data-testid="location-later"
            >
              {c.later}
            </button>
            <button
              type="button"
              onClick={onAllow}
              disabled={busy}
              className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors text-[13px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2"
              data-testid="location-allow"
            >
              <MapPin className="w-4 h-4" />
              {c.allow}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPromptModal;
